// runImageEditJob — run a SINGLE image-edit job inline (from the chat), without
// any of ImageAnimatorModal's component/localStorage/box coupling: POST to the
// operation's endpoint, poll the image-editing job until it terminates, return
// the result image URL. Used by EditImageToolUI's inline-edit mode.
//
// ponytail: duplicates the modal's POST+poll loop (~30 lines). The modal's own
// checkJobStatus is welded to setJob/localStorage/boxId, so a slim standalone
// runner is smaller and safer than refactoring the 4k-line modal. Consolidate
// only if a third caller appears.

import { getToken } from "../../../common/environment_config";

// IMAGE_EDIT_FEATURES / extractEditedImageUrls live in the 4k-line
// ImageAnimatorModal. Require them lazily (inside the functions that use them)
// so importing this module — e.g. from a unit test of buildEditJobBody — doesn't
// pull the whole modal and its icon/react-bootstrap deps at load time.

// Operations that run cleanly with just a source image (+ prompt / a simple
// scalar): one image in → one edited image out, no region-draw / reference
// upload / per-text-change UI. Everything else falls back to the popup.
export const INLINE_SUPPORTED_OPERATIONS = new Set([
  "generate-image-from-image",
  "generate-from-reference",
  "remove-object",
  "remove-background",
  "remove-text",
  "translate",
  "upscale",
  "resize",
]);

export function isInlineSupported(operation) {
  return INLINE_SUPPORTED_OPERATIONS.has(operation);
}

// Build the POST body for an inline edit. Pure — unit tested. Mirrors the
// per-feature bodies in ImageAnimatorModal.handleSubmit for the supported ops
// (region/reference-only fields are intentionally dropped; those ops aren't
// inline-supported).
export function buildEditJobBody(
  operation,
  { imageUrl, prompt, settings = {}, trainingId, boxId, navId, sceneIndex, narration } = {}
) {
  const p = prompt || settings.prompt || "";
  const base = { imageUrl, idealStudioTrainingId: trainingId, boxId, navId, sceneIndex };
  switch (operation) {
    case "generate-image-from-image":
      return { imageUrl, prompt: p, idealStudioTrainingId: trainingId, sceneIndex, boxId, navId };
    case "generate-from-reference":
      return {
        imageUrl,
        prompt: p,
        count: 1,
        provider: "gemini-nano",
        idealStudioTrainingId: trainingId,
        sceneIndex,
        boxId,
        navId,
        narration: narration || "",
      };
    case "remove-object":
      return { ...base, prompt: p };
    case "remove-text":
      return { ...base, mode: "all" };
    case "translate":
      return { ...base, targetLanguage: settings.targetLanguage || "Turkish", mode: "all" };
    case "upscale":
      return {
        imageUrl,
        scaleFactor: settings.scaleFactor || 2,
        idealStudioTrainingId: trainingId,
        sceneIndex,
        boxId,
        navId,
      };
    case "resize":
      return {
        imageUrl,
        aspectRatio: settings.aspectRatio || "16:9",
        idealStudioTrainingId: trainingId,
        sceneIndex,
        boxId,
        navId,
      };
    case "remove-background":
    default:
      return base;
  }
}

async function pollJob(apiUrl, jobId, signal) {
  const maxAttempts = 150;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) throw new Error("aborted");
    const res = await fetch(`${apiUrl}api/image-editing/job/${jobId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      signal,
    });
    if (!res.ok) throw new Error("Failed to check job status");
    const jobData = await res.json();
    const status = jobData?.status || jobData?.state || "unknown";
    if (status === "completed") {
      const { extractEditedImageUrls } = require("../merlin/ImageAnimatorModal");
      const url = jobData.result?.url || extractEditedImageUrls(jobData)[0] || null;
      if (!url) throw new Error("Job completed but no image URL");
      return url;
    }
    if (status === "failed" || status === "cancelled") {
      throw new Error(jobData.error || jobData.message || "Job failed");
    }
    await new Promise((r) => setTimeout(r, 4000));
  }
  throw new Error("Timed out waiting for the edit job");
}

// Run the edit end-to-end. Resolves to the result image URL, rejects on error.
// `onJobId` (optional) receives the middleware job id as soon as it exists, so
// the caller can approve that job once the result is applied.
export async function runImageEditJob({
  operation,
  imageUrl,
  prompt,
  settings,
  boxId,
  navId,
  sceneIndex,
  narration,
  signal,
  onJobId,
} = {}) {
  const apiUrl = process.env.MW_API_URL;
  if (!apiUrl) throw new Error("API URL not configured");
  if (!imageUrl) throw new Error("No source image URL");
  const { IMAGE_EDIT_FEATURES } = require("../merlin/ImageAnimatorModal");
  const feature = IMAGE_EDIT_FEATURES.find((f) => f.id === operation);
  if (!feature || !feature.endpoint) throw new Error(`Unsupported inline operation: ${operation}`);

  const trainingId = new URLSearchParams(window.location.search).get("id");
  const body = buildEditJobBody(operation, {
    imageUrl,
    prompt,
    settings,
    trainingId,
    boxId,
    navId,
    sceneIndex,
    narration,
  });

  const res = await fetch(`${apiUrl}${feature.endpoint}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || "Failed to start image edit");
  }
  const data = await res.json();
  const jobId = data?.jobId;
  if (!jobId) throw new Error("No jobId returned from edit request");
  onJobId?.(jobId);
  return pollJob(apiUrl, jobId, signal);
}
